import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { Field, RuntimeSession, WebsiteConfig } from "../types/core";
import type { RuntimeThemeContextValue } from "../types/theme";
import { resolveThemeFromWebsiteConfig } from "../../lib/theme";

const WBThemeContext = createContext<RuntimeThemeContextValue | undefined>(
  undefined,
);

function unwrapMaybeQuoted(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === "string") return parsed.trim();
  } catch {
    // Keep original if not JSON string.
  }
  return trimmed;
}

function nonEmpty(value: string | undefined | null): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = unwrapMaybeQuoted(value);
  return normalized.length > 0 ? normalized : undefined;
}

function coalesceString(
  ...values: Array<string | undefined | null>
): string | undefined {
  for (const value of values) {
    const normalized = nonEmpty(value);
    if (normalized !== undefined) return normalized;
  }
  return undefined;
}



type Props = {
  children: ReactNode;
  websiteConfig: WebsiteConfig;
  fields: Field[];
  session: RuntimeSession;
  registrationType?: string;
  hasWhiteLabelAccess?: boolean;
  /** IANA timezone of the event. When omitted, `useEventTimezone()` falls
   *  back to the browser's tz. */
  eventTimezone?: string;
};

export function WBThemeRuntimeProvider({
  children,
  websiteConfig,
  fields,
  session,
  registrationType,
  hasWhiteLabelAccess = false,
  eventTimezone,
}: Props) {
  const resolved = useMemo(
    () => resolveThemeFromWebsiteConfig(websiteConfig),
    [websiteConfig],
  );

  const contextValue = useMemo<RuntimeThemeContextValue>(
    () => ({
      currentColorPalette: {
        primary:
          coalesceString(websiteConfig.primaryColor, "#000000") ?? "#000000",
        secondary:
          coalesceString(websiteConfig.secondaryColor, "#7B7B7B") ?? "#7B7B7B",
        background:
          coalesceString(websiteConfig.backgroundColor, "#FFFFFF") ?? "#FFFFFF",
      },
      currentFonts: {
        header:
          coalesceString(
            websiteConfig.headerFont,
            "Tahoma, Geneva, sans-serif",
          ) ?? "Tahoma, Geneva, sans-serif",
        body:
          coalesceString(
            websiteConfig.bodyFont,
            "Inter, system-ui, sans-serif",
          ) ?? "Inter, system-ui, sans-serif",
      },
      currentRounding:
        coalesceString(websiteConfig.rounding, "rounded-md") ?? "rounded-md",
      bodyTheme: resolved.bodyTheme,
      headingsTheme: resolved.headingsTheme,
      buttonsTheme: resolved.buttonsTheme,
      hyperlinkTheme: resolved.hyperlinkTheme,
      formInputTheme: resolved.formInputTheme,
      customCss: resolved.customCss,
      homeSlug: nonEmpty(websiteConfig.homeSlug),
      fields,
      session,
      ...(registrationType ? { registrationType } : {}),
      loginAfterRegister: websiteConfig.loginAfterRegister,
      notifyEmail: websiteConfig.notifyEmail,
      editMode: false,
      hasWhiteLabelAccess,
      ...(eventTimezone ? { eventTimezone } : {}),
    }),
    [websiteConfig, resolved, fields, session, registrationType, hasWhiteLabelAccess, eventTimezone],
  );

  useEffect(() => {
    const id = "event-custom-css";
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = contextValue.customCss;

    return () => {
      if (style && style.id === id && !contextValue.customCss) {
        style.remove();
      }
    };
  }, [contextValue.customCss]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--wb-primary",
      contextValue.currentColorPalette.primary,
    );
    root.style.setProperty(
      "--wb-secondary",
      contextValue.currentColorPalette.secondary,
    );
    root.style.setProperty(
      "--wb-background",
      contextValue.currentColorPalette.background,
    );
    root.style.setProperty(
      "--wb-font-header",
      contextValue.currentFonts.header,
    );
    root.style.setProperty("--wb-font-body", contextValue.currentFonts.body);
  }, [contextValue]);

  return (
    <WBThemeContext.Provider value={contextValue}>
      {children}
    </WBThemeContext.Provider>
  );
}

export function useWBTheme() {
  const context = useContext(WBThemeContext);
  if (!context) {
    throw new Error("useWBTheme must be used within WBThemeRuntimeProvider");
  }
  return context;
}

/**
 * Resolve the active event timezone for DATE/DATE_TIME inputs and display
 * formatters. Defensive — never throws. Returns the event's IANA tz when
 * the bootstrap envelope provided it, otherwise the browser's runtime tz.
 */
export function useEventTimezone(): string {
  const ctx = useContext(WBThemeContext);
  return (
    ctx?.eventTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}
